import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Compass, TrendingDown, TrendingUp, Sparkles, ArrowRight, ListChecks, AlertTriangle, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';

const COLOR_DOT = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-rose-500' };
const COLOR_TEXT = { green: 'text-emerald-600', yellow: 'text-amber-600', red: 'text-rose-600' };

function GoalForm({ onSaved }) {
  const [goal, setGoal] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!goal || Number(goal) <= 0) {
      toast.error('Enter a monthly boarded-merchant goal greater than 0');
      return;
    }
    setSaving(true);
    try {
      await api.setSalesGPSGoal({ monthly_boarded_goal: Number(goal) });
      toast.success('Goal set — your Sales GPS is now active');
      onSaved();
    } catch (e) {
      toast.error('Could not save goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-8 text-center space-y-4" data-testid="sales-gps-goal-form">
      <Compass className="h-10 w-10 mx-auto text-primary" />
      <h2 className="text-xl font-heading font-bold">Set your monthly boarded-merchant goal</h2>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        Tell us how many merchants you want boarded this month. We'll reverse-engineer the exact
        daily activity — prospects, calls, conversations, demos, applications — you need to hit it.
      </p>
      <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
        <Input
          type="number"
          min="1"
          placeholder="e.g. 10"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          data-testid="sales-gps-goal-input"
        />
        <Button onClick={save} disabled={saving} data-testid="sales-gps-goal-save-btn">
          {saving ? 'Saving...' : 'Activate'}
        </Button>
      </div>
    </Card>
  );
}

function Scorecard({ scorecard }) {
  return (
    <Card className="p-6" data-testid="sales-gps-scorecard">
      <h3 className="font-semibold text-lg mb-1">Today's Activity Scorecard</h3>
      <p className="text-sm text-muted-foreground mb-4">Target vs. actual, recalculated daily against your remaining goal</p>
      <div className="space-y-3">
        {scorecard.map((row) => (
          <div key={row.stage} className="flex items-center gap-3" data-testid={`scorecard-row-${row.stage}`}>
            <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[row.color]}`} />
            <span className="text-sm font-medium w-40 flex-shrink-0">{row.label}</span>
            <Progress value={row.target > 0 ? Math.min(100, (row.actual / row.target) * 100) : 0} className="h-2 flex-1" />
            <span className={`text-sm font-semibold w-20 text-right flex-shrink-0 ${COLOR_TEXT[row.color]}`}>
              {row.actual}/{row.target}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function GoalProgress({ progress, requiredMonthly, monthlyGoal }) {
  return (
    <Card className="p-6" data-testid="sales-gps-goal-progress">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Monthly Goal Progress</h3>
          <p className="text-sm text-muted-foreground">{progress.working_days_remaining} working day{progress.working_days_remaining === 1 ? '' : 's'} left this month</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold" data-testid="sales-gps-boarded-mtd">{progress.boarded_mtd}<span className="text-muted-foreground text-lg">/{monthlyGoal}</span></div>
          <div className="text-xs text-muted-foreground">boarded this month</div>
        </div>
      </div>
      <Progress value={progress.pct_complete} className="h-3 mb-4" />
      <div className="grid grid-cols-4 gap-3 text-center text-sm">
        {['applications', 'demos', 'qualified', 'contacts'].map((s) => (
          <div key={s} className="p-2 rounded-lg bg-muted/50">
            <div className="text-lg font-bold">{requiredMonthly[s]}</div>
            <div className="text-xs text-muted-foreground capitalize">{s} needed / mo</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CoachingCard({ coaching }) {
  if (!coaching) {
    return (
      <Card className="p-6 border-emerald-200 bg-emerald-50/50" data-testid="sales-gps-coaching-ontrack">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-emerald-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-emerald-800">You're on track</h3>
            <p className="text-sm text-emerald-700">No stage is underperforming against the team average right now. Keep it up.</p>
          </div>
        </div>
      </Card>
    );
  }
  const b = coaching.bottleneck;
  return (
    <Card className="p-6 border-amber-200 bg-amber-50/50" data-testid="sales-gps-coaching-card">
      <div className="flex items-start gap-3">
        <Sparkles className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-amber-900">Today's Coaching Focus</h3>
            <Badge variant="outline" className="text-xs border-amber-400 text-amber-800 capitalize">
              {b.from_stage} &rarr; {b.to_stage}
            </Badge>
          </div>
          <p className="text-sm text-amber-900 mb-2" data-testid="sales-gps-coaching-message">{coaching.message}</p>
          <div className="flex items-center gap-1 text-xs text-amber-700">
            <TrendingDown className="h-3 w-3" />
            Your rate: <strong>{b.rep_rate}%</strong> vs team average <strong>{b.team_rate}%</strong>
          </div>
        </div>
      </div>
    </Card>
  );
}

function FunnelBreakdown({ diagnosis }) {
  const stages = [
    ['prospects', 'contacts', 'prospect_to_contact'],
    ['contacts', 'conversations', 'contact_to_conversation'],
    ['conversations', 'qualified', 'conversation_to_qualified'],
    ['qualified', 'demos', 'qualified_to_demo'],
    ['demos', 'analyses', 'demo_to_analysis'],
    ['analyses', 'applications', 'analysis_to_application'],
    ['applications', 'boarded', 'application_to_boarded'],
  ];
  return (
    <Card className="p-6" data-testid="sales-gps-funnel-breakdown">
      <h3 className="font-semibold text-lg mb-1">Your Funnel vs. Team (last {diagnosis.period_days} days)</h3>
      <p className="text-sm text-muted-foreground mb-4">Where you're converting well, and where you're not</p>
      <div className="space-y-2">
        {stages.map(([from, to, key]) => {
          const rep = diagnosis.rep_rates[key];
          const team = diagnosis.team_rates[key];
          const flagged = diagnosis.flagged.find((f) => f.transition === key);
          const repPct = rep.rate != null ? Math.round(rep.rate * 100) : null;
          const teamPct = team.rate != null ? Math.round(team.rate * 100) : null;
          return (
            <div key={key} className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${flagged ? 'bg-rose-50' : 'bg-muted/40'}`} data-testid={`funnel-transition-${key}`}>
              <span className="capitalize font-medium flex items-center gap-1.5">
                {from} <ArrowRight className="h-3 w-3 text-muted-foreground" /> {to}
              </span>
              <span className="flex items-center gap-3 text-xs">
                <span>You: <strong className={flagged ? 'text-rose-600' : ''}>{repPct != null ? `${repPct}%` : '—'}</strong> ({rep.denominator})</span>
                <span className="text-muted-foreground">Team: {teamPct != null ? `${teamPct}%` : '—'}</span>
                {flagged && <Badge variant="destructive" className="text-[10px]">Below range</Badge>}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BaselineRatesEditor() {
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await api.getSalesGPSBaselineRates().catch(() => null);
    if (res) setRates(res.rates);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSalesGPSBaselineRates(rates);
      toast.success('Tenant baseline conversion rates updated');
    } catch (e) {
      toast.error('Could not update rates');
    } finally {
      setSaving(false);
    }
  };

  if (!rates) return null;
  const labels = {
    prospect_to_contact: 'Prospect → Contact', contact_to_conversation: 'Contact → Conversation',
    conversation_to_qualified: 'Conversation → Qualified', qualified_to_demo: 'Qualified → Demo',
    demo_to_analysis: 'Demo → Analysis', analysis_to_application: 'Analysis → Application',
    application_to_boarded: 'Application → Boarded',
  };
  return (
    <Card className="p-6" data-testid="sales-gps-baseline-editor">
      <h3 className="font-semibold text-lg mb-1">Tenant Baseline Conversion Rates</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Used for new reps who don't yet have enough of their own activity history — the system
        automatically switches to a rep's own rate once they log 10+ conversions at a stage.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.keys(labels).map((k) => (
          <div key={k}>
            <Label className="text-xs">{labels[k]}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" min="1" max="100" step="1"
                value={Math.round((rates[k] || 0) * 100)}
                onChange={(e) => setRates({ ...rates, [k]: Number(e.target.value) / 100 })}
                data-testid={`baseline-rate-${k}`}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        ))}
      </div>
      <Button className="mt-4" onClick={save} disabled={saving} data-testid="save-baseline-rates-btn">
        {saving ? 'Saving...' : 'Save Baseline Rates'}
      </Button>
    </Card>
  );
}

function TeamSummary() {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSalesGPSTeamSummary().then((r) => setReps(r.reps || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-10 text-muted-foreground">Loading team...</div>;

  return (
    <Card className="p-6" data-testid="sales-gps-team-summary">
      <h3 className="font-semibold text-lg mb-4">Team Sales GPS</h3>
      {reps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No agents/subagents found on your team yet.</p>
      ) : (
        <div className="space-y-3">
          {reps.map((r) => (
            <div key={r.user_id} className="flex items-center justify-between gap-3 p-3 border rounded-lg" data-testid={`team-rep-${r.user_id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {r.monthly_goal ? `${r.boarded_mtd}/${r.monthly_goal} boarded this month` : 'No goal set'}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {r.monthly_goal && <Progress value={r.pct_complete} className="h-2 w-24" />}
                {r.primary_bottleneck ? (
                  <Badge variant="destructive" className="text-xs capitalize whitespace-nowrap flex-shrink-0">{r.primary_bottleneck.from_stage} &rarr; {r.primary_bottleneck.to_stage}</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs whitespace-nowrap flex-shrink-0">On track</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.getSalesGPSAlerts()
      .then((r) => { setAlerts(r.alerts || []); setTotal(r.total || (r.alerts || []).length); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (error) {
    return (
      <Card className="p-4 border-rose-200 bg-rose-50/50 text-sm text-rose-700" data-testid="sales-gps-alerts-error">
        Couldn't load pipeline alerts right now. Try refreshing the page.
      </Card>
    );
  }
  if (alerts.length === 0) return null;

  const visible = showAll ? alerts : alerts.slice(0, 6);

  return (
    <Card className="p-5 border-rose-200 bg-rose-50/50" data-testid="sales-gps-alerts">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-rose-600" />
        <h3 className="font-semibold text-rose-900">Pipeline Alerts</h3>
        <Badge variant="destructive" className="text-xs">{total}</Badge>
      </div>
      <div className="space-y-2">
        {visible.map((a, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-rose-900" data-testid={`sales-gps-alert-${i}`}>
            <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${a.severity === 'high' ? 'bg-rose-600' : 'bg-amber-500'}`} />
            {a.message}
          </div>
        ))}
      </div>
      {alerts.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 text-xs font-medium text-rose-700 hover:text-rose-900 underline"
          data-testid="sales-gps-alerts-toggle"
        >
          {showAll ? 'Show fewer' : `View all ${total} alerts`}
        </button>
      )}
    </Card>
  );
}

function NextTwentyQueue() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.getSalesGPSNext20()
      .then((r) => setQueue(r.queue || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleItemClick = (item) => {
    if (item.type === 'lead' && item.id) {
      navigate(`/leads/${item.id}`);
    } else if (item.type === 'follow_up' && item.list_id) {
      navigate(`/prospecting?list_id=${item.list_id}`);
    } else {
      navigate('/prospecting');
    }
  };

  return (
    <Card className="p-6" data-testid="sales-gps-next-20">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Your Next 20</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Ranked by intent, recency, and probability — tap any prospect to call or follow up</p>
      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading queue...</div>
      ) : error ? (
        <div className="text-sm text-rose-600 py-6 text-center" data-testid="sales-gps-next-20-error">Couldn't load your queue right now. Try refreshing the page.</div>
      ) : queue.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No follow-ups or stale leads right now — go add prospects.</div>
      ) : (
        <div className="space-y-2">
          {queue.map((item, i) => (
            <button
              type="button"
              key={`${item.type}-${item.id}-${i}`}
              onClick={() => handleItemClick(item)}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 hover:bg-muted/70 transition-colors text-left cursor-pointer"
              data-testid={`next-20-item-${i}`}
            >
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <PhoneCall className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{item.business_name || 'Prospect'}</div>
                <div className="text-xs text-muted-foreground truncate">{item.reason}</div>
              </div>
              <Badge variant="outline" className="text-[10px] uppercase flex-shrink-0">{item.type.replace('_', ' ')}</Badge>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function SalesGPSPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getSalesGPSDashboard();
      setData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="sales-gps-loading"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-gps-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Compass className="h-6 w-6 text-primary" /> Sales GPS</h1>
        <p className="text-sm text-muted-foreground mt-1">Goal &rarr; Required Funnel &rarr; Daily Targets &rarr; Coaching</p>
      </div>

      <Tabs defaultValue="me" className="w-full">
        <TabsList data-testid="sales-gps-tabs">
          <TabsTrigger value="me" data-testid="sales-gps-tab-me">My Sales GPS</TabsTrigger>
          {isAdmin && <TabsTrigger value="team" data-testid="sales-gps-tab-team">Team</TabsTrigger>}
          {isAdmin && <TabsTrigger value="settings" data-testid="sales-gps-tab-settings">Baseline Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="me" className="space-y-6 mt-4">
          {!data?.goal_set ? (
            <GoalForm onSaved={load} />
          ) : (
            <>
              <AlertsBanner />
              <GoalProgress progress={data.goal_progress} requiredMonthly={data.required_monthly} monthlyGoal={data.monthly_boarded_goal} />
              <CoachingCard coaching={data.coaching} />
              <NextTwentyQueue />
              <Scorecard scorecard={data.today_scorecard} />
              <FunnelBreakdown diagnosis={data.funnel_diagnosis} />
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid="change-goal-toggle">Change my monthly goal</summary>
                <div className="mt-3 max-w-sm">
                  <GoalForm onSaved={load} />
                </div>
              </details>
            </>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="team" className="mt-4">
            <TeamSummary />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="settings" className="mt-4">
            <BaselineRatesEditor />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
